// SPDX-License-Identifier: Apache-2.0

// VaultOrbit3D — the v2 SolidJS port of v1's React + r3f VaultOrbit.
//
// Top-level Solid component. Owns the canvas + the imperative engine that
// wires together every module under ./orbit/. Lifecycle is onMount/onCleanup;
// reactivity is handled via createEffect so VaultExperience's existing prop
// shape (docs, activeId, highlight, hoveredId, onSelect, onHover) continues
// to work.
//
// PROPS CONTRACT NOTE: the brief specified one props shape; the existing
// VaultExperience.tsx (which we MUST NOT modify per constraints) passes a
// slightly different shape. We accept the union — props the brief mandated
// (`edges`, `semanticEdges`, `embodiedMode`) are optional; props the existing
// VaultExperience uses (`highlight`, `hoveredId`) are also optional but
// honoured when present. `edges` defaults to `buildEdges(docs())` when not
// supplied so VaultExperience's call site keeps working.

import { onCleanup, onMount, createEffect, createSignal, Show } from "solid-js";
import * as THREE from "three";
import type { VaultDoc } from "@tether/storage";
import { buildEdges } from "../vaultLinks";
import { createOrbitScene, disposeOrbitScene } from "./scene";
import { CameraRig } from "./camera-rig";
import { createPostFX, type OrbitPostFX } from "./postprocessing";
import { createBrainCore } from "./brain-core";
import { createNodeField, type NodeField, type LayoutMode } from "./nodes";
import { createEdgeField, type SemanticEdge } from "./edges";
import { createActiveLink } from "./active-link";
import { DocLabelLayer } from "./doc-label";
import { createSatellites, type SatelliteLayer, type SatelliteSnapshot } from "./satellites";
import { createTextLayer, type TextLayer } from "./text-msdf";
import { EmbodiedRig } from "../embodied/EmbodiedRig";
import "./orbit.css";

// Edge tuple type — matches buildEdges()'s return shape and stays
// structurally compatible with anything Glaucus might push later.
export type Edge = [string, string];

export interface VaultOrbit3DProps {
  /** Reactive accessor: the doc list (vault state). */
  docs: () => VaultDoc[];
  /** Reactive accessor: explicit wikilink/markdown edges. Optional — when
   *  omitted, derived from docs() via buildEdges(). */
  edges?: () => Edge[];
  /** Optional semantic edge layer (Glaucus stream wires this in). */
  semanticEdges?: () => SemanticEdge[];
  /** Reactive accessor: currently selected doc id, or null. */
  activeId: () => string | null;
  /** Set of doc-ids the user is searching for; non-matching nodes dim. */
  highlight?: () => Set<string>;
  /** Hovered-id readback (when caller wants to drive its own UI from hover). */
  hoveredId?: () => string | null;
  /** Click callback — called with the clicked doc-id, or null for empty space. */
  onSelect: (id: string | null) => void;
  /** Hover callback — fired when the pointer enters/leaves a node. */
  onHover?: (id: string | null) => void;
  /** Optional: when true, hand camera control to Apollo's PointerLock rig. */
  embodiedMode?: () => boolean;
  /** Hephaestus 4 — Claude-session satellites orbiting the brain core.
   *  Source data is `useBrainDocs().state().docs` (filtered to live sessions
   *  by the consumer). Wiring is OUTSIDE this stream's scope: leave a
   *  comment for Hermes/Glaucus — see VaultExperience consumer. */
  sessions?: () => Array<{ id: string; title: string; lastTouched: number }>;
  /** Hephaestus 4 — pulse map keyed by sessionId, value = ms epoch of last
   *  tool-touch. Source: `useBrainDocs().state().pulses`. */
  pulses?: () => Map<string, number>;
  /** Hephaestus 4 — VaultTopbar's 'ring' | 'cluster' selector. Default ring. */
  layoutMode?: () => LayoutMode;
}

// CONSUMER WIRING (left for whoever owns VaultExperience.tsx):
//
//   const brain = useBrainDocs();
//   const sessions = () => brain.state().docs.map(d => ({
//     id: d.id, title: d.title ?? d.id, lastTouched: d.updatedAt ?? Date.now(),
//   }));
//   const pulses = () => brain.state().pulses;
//   <VaultOrbit3D ...existing props
//                 sessions={sessions} pulses={pulses}
//                 layoutMode={layoutSignal} />
//
// We don't add this to VaultExperience here because that file is owned by
// Iris 3 / others per the brief's "DO NOT touch VaultExperience" line.

// Re-export so VaultExperience's import-path-rename can be a one-liner if
// needed. (We default-export VaultOrbit3D for ergonomic re-export shimming.)
export default function VaultOrbit3D(props: VaultOrbit3DProps) {
  let canvasRef: HTMLCanvasElement | undefined;
  let containerRef: HTMLDivElement | undefined;
  const [errorMsg, setErrorMsg] = createSignal<string | null>(null);

  // Reactive state surface (read by DocLabelLayer)
  const [camera, setCamera] = createSignal<THREE.PerspectiveCamera | null>(null);
  const [size, setSize] = createSignal<{ w: number; h: number }>({ w: 0, h: 0 });
  const [hoveredLabel, setHoveredLabel] = createSignal<{
    doc: VaultDoc;
    pos: THREE.Vector3;
  } | null>(null);
  const [activeLabel, setActiveLabel] = createSignal<{
    doc: VaultDoc;
    pos: THREE.Vector3;
  } | null>(null);
  // Hephaestus 4 — satellite snapshot, refreshed each frame from the engine
  // so the DOM label layer can project rose-pill positions to screen space.
  const [satelliteSnap, setSatelliteSnap] = createSignal<SatelliteSnapshot[]>([]);
  // Apollo 4 — pointer-lock state mirror so the HUD knows whether to show
  // "click to fly" or "WASD · mouse · Esc". The rig itself owns the actual
  // engagement; this signal is read-only telemetry for the overlay below.
  const [pointerLocked, setPointerLocked] = createSignal(false);

  // Engine is set asynchronously after launch() resolves. Stored in a signal
  // (NOT a plain `let`) so reactive effects below re-run when it arrives.
  // Solid's createEffect only tracks deps that are READ during a run; effects
  // that early-returned on `if (!engine) return;` BEFORE reading their prop
  // accessor would skip dep registration on first run and never re-fire when
  // the prop changed later. Reading `engineSig()` first makes the effect
  // re-run when engine flips from null → non-null, at which point it can
  // also read the prop and register that dep.
  const [engineSig, setEngineSig] = createSignal<Engine | null>(null);

  onMount(() => {
    if (!canvasRef) return;
    void launch(canvasRef, props, {
      setCamera,
      setSize,
      setHoveredLabel,
      setActiveLabel,
      setSatelliteSnap,
    })
      .then((e) => {
        setEngineSig(e);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        setErrorMsg(msg);
        // eslint-disable-next-line no-console
        console.error("[VaultOrbit3D] launch failed:", err);
      });

    // Apollo 4 — mirror pointer-lock state into a signal so the HUD overlay
    // can flip between "click to fly" and "engaged" copy. EmbodiedRig also
    // listens to this event for its own state; both can coexist (DOM events
    // fan out to every listener).
    const onLockChange = () => {
      setPointerLocked(
        canvasRef !== undefined && document.pointerLockElement === canvasRef,
      );
    };
    document.addEventListener("pointerlockchange", onLockChange);
    onCleanup(() =>
      document.removeEventListener("pointerlockchange", onLockChange),
    );
  });

  // Reactive: docs change → rebuild sim + edges. Read engineSig FIRST so the
  // effect re-runs the moment engine becomes available, then read the props
  // so Solid can track them in the same run.
  createEffect(() => {
    const e = engineSig();
    const ds = props.docs();
    const eOverride = props.edges?.();
    if (!e) return;
    const wikiEdges = eOverride ?? buildEdges(ds);
    void e.syncDocs(ds, wikiEdges, props.semanticEdges?.() ?? []);
  });

  // Reactive: semantic edges only (cheap re-tint without sim rebuild)
  createEffect(() => {
    const e = engineSig();
    const sem = props.semanticEdges?.() ?? [];
    if (!e) return;
    e.setSemanticEdges(sem);
  });

  // Reactive: highlight set drives per-node tint
  createEffect(() => {
    const e = engineSig();
    const hl = props.highlight?.() ?? new Set<string>();
    const aid = props.activeId();
    if (!e) return;
    e.setHighlight(hl, aid);
  });

  // Reactive: active id drives brain pulse + active-link
  createEffect(() => {
    const e = engineSig();
    const aid = props.activeId();
    if (!e) return;
    e.setActive(aid);
  });

  // Reactive: embodied takeover handoff
  createEffect(() => {
    const e = engineSig();
    const flag = props.embodiedMode?.() ?? false;
    if (!e) return;
    e.setEmbodied(flag);
  });

  // Hephaestus 4 — sessions / pulses / layoutMode reactivity.
  createEffect(() => {
    const e = engineSig();
    const sessions = props.sessions?.() ?? [];
    if (!e) return;
    e.syncSatellites(sessions);
  });

  createEffect(() => {
    const e = engineSig();
    const pulses = props.pulses?.() ?? new Map<string, number>();
    if (!e) return;
    e.applyPulses(pulses);
  });

  createEffect(() => {
    const e = engineSig();
    const mode = props.layoutMode?.() ?? "ring";
    if (!e) return;
    e.setLayoutMode(mode);
  });

  onCleanup(() => {
    const engine = engineSig();
    if (engine) {
      engine.dispose();
      setEngineSig(null);
    }
  });

  return (
    <div
      ref={(el) => (containerRef = el)}
      class="vault-orbit"
      data-testid="vault-orbit"
    >
      <canvas
        ref={(el) => {
          canvasRef = el;
        }}
        class="vault-orbit__canvas"
      />
      <DocLabelLayer
        camera={camera}
        size={size}
        hovered={hoveredLabel}
        active={activeLabel}
        satellites={satelliteSnap}
      />
      <Show when={camera()}>
        {(cam) => (
          <EmbodiedRig
            camera={cam()}
            enabled={() => props.embodiedMode?.() ?? false}
            target={() => canvasRef ?? null}
          />
        )}
      </Show>
      {/* Apollo 4 — embodied HUD. Discoverability fix for the "what is
          embodied, it doesn't do anything" report: when the toggle is on
          but pointer-lock isn't engaged, tell the user to click. Once
          engaged, show the controls reference. Both states are pointer-events:
          none so they never block the canvas click that requests the lock. */}
      <Show when={(props.embodiedMode?.() ?? false) && !pointerLocked()}>
        <div class="vault-orbit__embodied-hint" data-testid="embodied-hint-click">
          <div class="vault-orbit__embodied-hint-title">embodied mode</div>
          <div class="vault-orbit__embodied-hint-body">
            click the canvas to fly · esc to release
          </div>
        </div>
      </Show>
      <Show when={(props.embodiedMode?.() ?? false) && pointerLocked()}>
        <div class="vault-orbit__embodied-hud" data-testid="embodied-hud-engaged">
          <span class="vault-orbit__embodied-key">W</span>
          <span class="vault-orbit__embodied-key">A</span>
          <span class="vault-orbit__embodied-key">S</span>
          <span class="vault-orbit__embodied-key">D</span>
          <span class="vault-orbit__embodied-sep">·</span>
          <span class="vault-orbit__embodied-key">space</span>
          <span class="vault-orbit__embodied-key">shift</span>
          <span class="vault-orbit__embodied-sep">·</span>
          <span class="vault-orbit__embodied-key">mouse</span>
          <span class="vault-orbit__embodied-sep">·</span>
          <span class="vault-orbit__embodied-key">esc</span>
        </div>
      </Show>
      {errorMsg() && (
        <div class="vault-orbit__error" data-testid="vault-orbit-error">
          orbit: {errorMsg()}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Engine — imperative core. The Solid component drives this via methods.
// ────────────────────────────────────────────────────────────────────────────

interface EngineDeps {
  setCamera: (c: THREE.PerspectiveCamera) => void;
  setSize: (s: { w: number; h: number }) => void;
  setHoveredLabel: (v: { doc: VaultDoc; pos: THREE.Vector3 } | null) => void;
  setActiveLabel: (v: { doc: VaultDoc; pos: THREE.Vector3 } | null) => void;
  /** Hephaestus 4 — pushed each frame so DocLabelLayer can render satellite pills. */
  setSatelliteSnap: (s: SatelliteSnapshot[]) => void;
}

interface Engine {
  syncDocs(docs: VaultDoc[], wikiEdges: Edge[], semEdges: SemanticEdge[]): Promise<void>;
  setSemanticEdges(sem: SemanticEdge[]): void;
  setHighlight(ids: Set<string>, activeId: string | null): void;
  setActive(id: string | null): void;
  setEmbodied(embodied: boolean): void;
  /** Hephaestus 4 */
  syncSatellites(sessions: Array<{ id: string; title: string; lastTouched: number }>): void;
  applyPulses(pulses: Map<string, number>): void;
  setLayoutMode(mode: LayoutMode): void;
  dispose(): void;
}

async function launch(
  canvas: HTMLCanvasElement,
  props: VaultOrbit3DProps,
  deps: EngineDeps,
): Promise<Engine> {
  // ── Scene + post-processing ─────────────────────────────────────────────
  const sceneSetup = createOrbitScene(canvas);
  const { renderer, scene, camera } = sceneSetup;
  deps.setCamera(camera);

  // Brain core (also the godrays target)
  const brainCore = createBrainCore();
  scene.add(brainCore.group);

  // Node field — async because it tries to acquire a WebGPU device.
  const nodes: NodeField = await createNodeField({ maxNodes: 4096 });
  scene.add(nodes.mesh);

  // Edge field
  const edges = createEdgeField();
  scene.add(edges.wikilinkLines);
  scene.add(edges.semanticLines);

  // Active-link (tube + particles from active node to brain core)
  const activeLink = createActiveLink();
  scene.add(activeLink.group);

  // Hephaestus 4 — satellites layer (rose-tinted Claude-session orbiters).
  // Brain core sits at world origin (createBrainCore has no offset).
  const brainCorePos = new THREE.Vector3(0, 0, 0);
  const satellites: SatelliteLayer = createSatellites(scene, brainCorePos);

  // Hephaestus 4 — MSDF text overlay (always-on doc titles for close panels).
  const textLayer: TextLayer = createTextLayer();
  scene.add(textLayer.group);

  // Tracks the last per-session pulse ts we've forwarded into the satellite
  // layer, so applyPulses can fire only on rising-edge changes.
  const lastPulseFwd = new Map<string, number>();

  // Camera rig — pointer-driven base + sinusoidal bob
  const cameraRigState = { pointer: new THREE.Vector2(0, 0), pointerActive: false };
  const cameraRig = new CameraRig(camera, cameraRigState);

  // Post-processing AFTER brainCore is in-scene so godRays can bind to it.
  let postFX: OrbitPostFX | null = null;
  try {
    postFX = createPostFX(renderer, scene, camera, brainCore.core);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[VaultOrbit3D] postprocessing init failed; falling back to plain render", err);
  }

  // ── Active-id + hovered-id state (CPU-side) ─────────────────────────────
  let activeId: string | null = null;
  let hoveredId: string | null = null;

  // ── Pointer / raycast ───────────────────────────────────────────────────
  const raycaster = new THREE.Raycaster();
  raycaster.params.Points = { threshold: 0.2 };

  function ndcFromEvent(e: PointerEvent) {
    const rect = canvas.getBoundingClientRect();
    cameraRigState.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    cameraRigState.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    cameraRigState.pointerActive = true;
  }

  function raycastHover() {
    if (nodes.count() === 0) {
      if (hoveredId !== null) {
        hoveredId = null;
        deps.setHoveredLabel(null);
        props.onHover?.(null);
        canvas.style.cursor = "";
      }
      return;
    }
    raycaster.setFromCamera(cameraRigState.pointer, camera);
    const id = nodes.raycast(raycaster);
    if (id !== hoveredId) {
      hoveredId = id;
      props.onHover?.(id);
      canvas.style.cursor = id ? "pointer" : "";
      if (id) {
        const idx = nodes.idToIdx(id);
        const doc = nodes.doc(id);
        if (idx !== undefined && doc) {
          const pos = readNodeWorldPos(idx);
          deps.setHoveredLabel({ doc, pos });
        }
      } else {
        deps.setHoveredLabel(null);
      }
    } else if (id) {
      // Same hover — update pos so label tracks the moving sphere.
      const idx = nodes.idToIdx(id);
      const doc = nodes.doc(id);
      if (idx !== undefined && doc) {
        const pos = readNodeWorldPos(idx);
        deps.setHoveredLabel({ doc, pos });
      }
    }
  }

  function onPointerMove(e: PointerEvent) {
    ndcFromEvent(e);
    raycastHover();
  }
  function onPointerLeave() {
    cameraRigState.pointerActive = false;
    if (hoveredId !== null) {
      hoveredId = null;
      props.onHover?.(null);
      deps.setHoveredLabel(null);
      canvas.style.cursor = "";
    }
  }
  function onPointerDown(e: PointerEvent) {
    ndcFromEvent(e);
    raycastHover();
    if (hoveredId) {
      e.preventDefault();
      props.onSelect(hoveredId);
    }
  }
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerleave", onPointerLeave);
  canvas.addEventListener("pointerdown", onPointerDown);

  // ── Resize ─────────────────────────────────────────────────────────────
  function updateSize() {
    const w =
      canvas.clientWidth ||
      canvas.parentElement?.clientWidth ||
      window.innerWidth;
    const h =
      canvas.clientHeight ||
      canvas.parentElement?.clientHeight ||
      window.innerHeight;
    if (w === 0 || h === 0) return;
    renderer.setSize(w, h, false);
    if (postFX) postFX.resize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    deps.setSize({ w, h });
  }
  const onResize = () => updateSize();
  window.addEventListener("resize", onResize);
  // ResizeObserver for container changes (e.g. side rail toggling)
  let resizeObs: ResizeObserver | null = null;
  if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
    resizeObs = new ResizeObserver(() => updateSize());
    resizeObs.observe(canvas.parentElement);
  }
  updateSize();

  // ── Helper: read a node's world position as a fresh THREE.Vector3 ───────
  const tmpReadVec = new THREE.Vector3();
  function readNodeWorldPos(idx: number): THREE.Vector3 {
    const positions = nodes.positions;
    tmpReadVec.set(
      positions[idx * 4 + 0] ?? 0,
      positions[idx * 4 + 1] ?? 0,
      positions[idx * 4 + 2] ?? 0,
    );
    return tmpReadVec.clone();
  }

  // ── Frame loop ──────────────────────────────────────────────────────────
  let stopped = false;
  let lastTime = performance.now();
  const startTime = lastTime;

  async function frame() {
    if (stopped) return;
    const now = performance.now();
    const dtSec = (now - lastTime) / 1000;
    const elapsedSec = (now - startTime) / 1000;
    lastTime = now;

    // Force-sim step + matrix push
    await nodes.step(dtSec, hoveredId, activeId);

    // Brain core animation
    brainCore.update(dtSec, elapsedSec);

    // Camera rig
    cameraRig.update(elapsedSec);

    // Edges follow node positions
    edges.updatePositions(nodes.positions, nodes.idToIdx);

    // Active-link tube + particles
    let activeWorldPos: THREE.Vector3 | null = null;
    if (activeId) {
      const idx = nodes.idToIdx(activeId);
      if (idx !== undefined) activeWorldPos = readNodeWorldPos(idx);
    }
    activeLink.update(elapsedSec, activeWorldPos);

    // Hephaestus 4 — satellites + text-msdf updates
    satellites.update(dtSec, elapsedSec);
    deps.setSatelliteSnap(satellites.snapshot());

    // Text layer: enumerate every node, let the layer pick the closest N
    // by camera distance and render Text meshes for them.
    const enumNodes = nodes.enumerateNodes();
    textLayer.updateTextLayer(camera, enumNodes);

    // Update active label position too
    if (activeId) {
      const idx = nodes.idToIdx(activeId);
      const doc = nodes.doc(activeId);
      if (idx !== undefined && doc) {
        deps.setActiveLabel({ doc, pos: readNodeWorldPos(idx) });
      } else {
        deps.setActiveLabel(null);
      }
    } else {
      deps.setActiveLabel(null);
    }

    // Re-raycast every frame so a moving sim still resolves hover targets
    if (cameraRigState.pointerActive) raycastHover();

    // Render
    if (postFX) {
      postFX.composer.render();
    } else {
      renderer.render(scene, camera);
    }
    requestAnimationFrame(() => void frame());
  }
  requestAnimationFrame(() => void frame());

  // Serialize syncDocs() calls — Solid's createEffect on docs() can fire many
  // times in quick succession (e.g. demo seed adds 20 docs one-at-a-time),
  // and overlapping rebuilds race the GPU buffer lifecycle (await
  // setupForceLayout() yields, a second rebuild tears down the in-progress
  // setup, validation throws). Each new call awaits the previous.
  let syncQueue: Promise<unknown> = Promise.resolve();

  // ── Public surface ──────────────────────────────────────────────────────
  return {
    async syncDocs(docs: VaultDoc[], wikiEdges: Edge[], semEdges: SemanticEdge[]) {
      const prev = syncQueue;
      const next = (async () => {
        try {
          await prev;
        } catch {
          /* prior rebuild errored — chain forward anyway */
        }
        await nodes.rebuild(docs, wikiEdges);
        edges.rebuild(docs, wikiEdges, semEdges);
        cameraRig.setDocCount(docs.length);
        // Hephaestus 4 — refresh text layer's node-meta cache. The frame loop
        // reads positions every tick; this just primes title/tint per id.
        textLayer.syncNodes(nodes.enumerateNodes());
      })();
      syncQueue = next;
      await next;
    },
    setSemanticEdges(sem: SemanticEdge[]) {
      // Re-run rebuild with the cached doc list — cheap because nodes don't
      // re-rebuild, only the semantic line buffers do.
      // We don't keep a doc cache here; edges.rebuild needs docs. Read from
      // nodes.doc() per-id, but that'd require iterating idxToId. Simpler: a
      // full rebuild is fine since semantic edges are batched (Glaucus debounces).
      // Cheap-out: mutate semantic geometry via rebuild after looking up docs.
      const docs: VaultDoc[] = [];
      for (let i = 0; i < nodes.count(); i++) {
        const id = nodes.idxToId(i);
        if (!id) continue;
        const d = nodes.doc(id);
        if (d) docs.push(d);
      }
      // Wikilink edges aren't owned here — re-derive from docs
      edges.rebuild(docs, buildEdges(docs), sem);
    },
    setHighlight(ids: Set<string>, aid: string | null) {
      nodes.applyHighlight(ids, aid);
      edges.applyFocus(hoveredId, aid);
    },
    setActive(id: string | null) {
      activeId = id;
      brainCore.setActive(id !== null);
      const doc = id ? nodes.doc(id) ?? null : null;
      activeLink.setActive(id, doc);
      edges.applyFocus(hoveredId, id);
    },
    setEmbodied(embodied: boolean) {
      // Embodied takeover: flip cameraRig.takeover so the auto-bob loop stops
      // touching camera.position/.quaternion, then EmbodiedRig (mounted in the
      // JSX above, sharing the same camera via the `camera` signal) drives the
      // camera via PointerLock + WASD. When `embodied` flips back to false the
      // rig's createEffect disengages and cameraRig resumes the bob.
      cameraRig.takeover = embodied;
    },
    syncSatellites(sessions) {
      satellites.syncSessions(sessions);
    },
    applyPulses(pulses: Map<string, number>) {
      // Forward only rising-edge pulses to the satellites layer so the
      // mesh's emissive bump triggers once per tool_use, not every frame.
      for (const [id, ts] of pulses) {
        const last = lastPulseFwd.get(id) ?? 0;
        if (ts > last) {
          satellites.pulse(id);
          lastPulseFwd.set(id, ts);
        }
      }
      // GC stale ids (session removed → drop from forwarded map).
      for (const id of [...lastPulseFwd.keys()]) {
        if (!pulses.has(id)) lastPulseFwd.delete(id);
      }
    },
    setLayoutMode(mode: LayoutMode) {
      nodes.setLayoutMode(mode);
    },
    dispose() {
      stopped = true;
      window.removeEventListener("resize", onResize);
      resizeObs?.disconnect();
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerleave", onPointerLeave);
      canvas.removeEventListener("pointerdown", onPointerDown);
      activeLink.dispose();
      edges.dispose();
      nodes.dispose();
      brainCore.dispose();
      satellites.dispose();
      textLayer.dispose();
      postFX?.dispose();
      disposeOrbitScene(sceneSetup);
    },
  };
}
